import type { ReactNode } from 'react';
import { OutcomeBadge, RecordingIndicator } from '@/components/ds-zta';
import { cn } from '@/lib/cn';
import type { TerminalChromeStatus } from './connectionStatus';

export type { TerminalChromeStatus };

/* ZTA design evaluation F40 — the one toolbar around a terminal screen.
 *
 * `.zta-term-chrome` (generated into styles/tokens-zta.css by
 * scripts/design/term-chrome-from-tokens.py) pins the d2d tokens to their
 * dark-theme values, so the bar and the screen read as one dark surface in
 * both app themes. Everything inside uses the normal tokens.
 *
 *   title      the terminal's name: "kubectl", "SSH Terminal"…
 *   target     what it is connected to (cluster, server), in mono
 *   status     the connection state, as an OutcomeBadge chip
 *   recording  shows the RecordingIndicator ("Recording")
 *   actions    the right-hand buttons
 *   toolbar    a second row under the bar (e.g. the username form)
 *   footer     the status bar under the screen
 *
 * `as="main"` + `headingLevel={1}` for the full-screen routes, which have no
 * app shell around them: the page then has a main landmark and an h1. */

export interface TerminalChromeProps {
    /** Omit inside a dialog whose own title already names the terminal. */
    title?: ReactNode;
    target?: ReactNode;
    status?: TerminalChromeStatus;
    recording?: boolean;
    actions?: ReactNode;
    toolbar?: ReactNode;
    footer?: ReactNode;
    as?: 'div' | 'main';
    /** Render the title as a heading of this level; omit for a plain label. */
    headingLevel?: 1 | 2 | 3;
    className?: string;
    children: ReactNode;
}

export default function TerminalChrome({
    title, target, status, recording, actions, toolbar, footer, as = 'div', headingLevel, className, children,
}: TerminalChromeProps) {
    const Root = as;
    const Heading = headingLevel ? (`h${headingLevel}` as const) : 'span';
    return (
        <Root className={cn('zta-term-chrome flex min-h-0 flex-col overflow-hidden bg-[var(--d2d-bg-primary)]', className)}>
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] px-4 py-1.5">
                <div className="flex min-w-0 items-center gap-3">
                    {title && (
                        <Heading className="m-0 flex min-w-0 items-center gap-3 text-sm font-normal">
                            <span className="font-mono font-semibold text-[var(--brand)]">{title}</span>
                            {target && (
                                <>
                                    <span aria-hidden="true" className="text-[var(--d2d-text-muted)]">—</span>
                                    <span className="truncate font-mono text-[var(--d2d-text-primary)]">{target}</span>
                                </>
                            )}
                        </Heading>
                    )}
                    {status && <OutcomeBadge value={status.value} shape="chip">{status.label}</OutcomeBadge>}
                    {recording && <RecordingIndicator />}
                </div>
                {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
            </div>
            {toolbar}
            {children}
            {footer && (
                <div className="flex shrink-0 items-center justify-between gap-4 border-t border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] px-4 py-1 text-micro text-[var(--d2d-text-muted)]">
                    {footer}
                </div>
            )}
        </Root>
    );
}
